import type { BilingualLabel } from '@/i18n';
import { CONTENT_PAGES } from '@/lib/settings-schema';

/**
 * The content pages a header-menu item can point at — the pages the shop
 * serves at `/pages/<slug>` from the `pages.content` setting.
 *
 * The usual ones first, named in both languages; then any page the store
 * added of its own, by its address; then whatever an item already points at
 * that neither list has, so opening the menu never silently re-points it.
 */

export interface PageChoice {
  slug: string;
  label: BilingualLabel;
  /**
   * Whether the shop has the page. It shows one with a title and a body in
   * some language; anything less is a "not found" for whoever follows the link.
   */
  written: boolean;
}

export function contentPageChoices(stored: unknown, inUse: readonly string[] = []): PageChoice[] {
  const pages = stored && typeof stored === 'object' ? (stored as Record<string, unknown>) : {};
  const known = new Set(CONTENT_PAGES.map((page) => page.slug));

  const usual = CONTENT_PAGES.map((page) => ({ slug: page.slug, label: page.label, written: isWritten(pages[page.slug]) }));
  const own = Object.keys(pages)
    .filter((slug) => !known.has(slug))
    .map((slug) => {
      known.add(slug);
      return { slug, label: { ar: slug, en: slug }, written: isWritten(pages[slug]) };
    });
  const pointedAt = [...new Set(inUse)]
    .filter((slug) => slug && !known.has(slug))
    .map((slug) => ({ slug, label: { ar: slug, en: slug }, written: false }));

  return [...usual, ...own, ...pointedAt];
}

/** The shop's own test for a page (apps/storefront `pageText`): a title and a body in at least one language. */
export function isWritten(entry: unknown): boolean {
  if (!entry || typeof entry !== 'object') return false;
  return Object.values(entry as Record<string, unknown>).some((text) => {
    if (!text || typeof text !== 'object') return false;
    const { title, body } = text as { title?: unknown; body?: unknown };
    return typeof title === 'string' && title.trim() !== '' && typeof body === 'string' && body.trim() !== '';
  });
}
